import type { ItEmailItemPublic, ItEmailPublic } from '@accounts/shared';
import { type CSSProperties, type ReactNode, useState } from 'react';

import { STR } from '../../../../i18n';
import { formatDateTime, relativeTime } from '../../../../lib/format';
import { Card, EmptyRow } from '../../../../ui/Primitives';
import { monoStyle, quietButtonStyle, rowActionStyle } from '../../../../ui/styles';

import { toneBar, toneOfAction } from './tone';
import { ToneWord } from './ToneParts';
import { useItEmailActions } from './useItEmails';
import { waitingOnIt } from './waiting';

const ellipsis: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const overdueBar = 'inset 3px 0 0 var(--bg-danger)';

const itemGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '24px 96px minmax(120px,.9fr) minmax(140px,1fr) minmax(160px,1.3fr) auto',
  gap: 10,
  alignItems: 'center',
  padding: '7px 16px',
  borderBottom: '1px solid var(--border)',
  fontSize: 12.5,
};

/**
 * WAITING ON IT (spec row 15; FINAL demo R3-2): one block per SENT email that still has
 * open items, oldest first, its items under it in their Set up / Remove tone. Unsent drafts
 * sit at the top as one line each: their items have not left their lanes. Overdue is the
 * server's (`overdueAfterDays` after the email was sent).
 *
 * Every action here is the IT email's own (per-item Mark granted / removed, IT confirmed
 * all, Take back), never the Queue's bulk route, so the email and the access record move
 * together in one server transaction.
 */
export function WaitingOnItLane({
  emails,
  overdueAfterDays,
  onOpen,
}: {
  emails: readonly ItEmailPublic[];
  overdueAfterDays: number | null;
  onOpen: (email: ItEmailPublic) => void;
}) {
  const [closed, setClosed] = useState(false);
  const actions = useItEmailActions();
  const busy = actions.isPending;

  const drafts = emails.filter((email) => email.status === 'draft');
  const { emails: waiting, open: openItems, overdue: overdueItems } = waitingOnIt(emails);

  function itemLine(email: ItEmailPublic, item: ItEmailItemPublic): ReactNode {
    const tone = toneOfAction(item.action);
    return (
      <li key={item.id} className="row" style={{ ...itemGrid, paddingLeft: 32, boxShadow: toneBar(tone) }}>
        <span />
        <ToneWord tone={tone} />
        <span style={ellipsis}>
          <span style={{ fontWeight: 650 }}>{item.user.name}</span>
          <span className="tx-subtle" style={{ display: 'block', fontSize: 11.5, ...ellipsis }}>
            {item.user.email}
          </span>
        </span>
        <span style={ellipsis}>{item.system.name}</span>
        <span style={{ ...ellipsis, fontSize: 12 }}>
          {item.login === null ? (
            <span className="tx-subtle">{STR.accessCommon.noLogin}</span>
          ) : (
            <span style={{ ...monoStyle, fontSize: 12 }}>{item.login}</span>
          )}
        </span>
        <span style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="tbtn"
            style={rowActionStyle}
            disabled={busy}
            onClick={() => {
              actions.mutate({ kind: 'item', email, itemId: item.id, action: item.action });
            }}
          >
            {item.action === 'set_up' ? STR.access.queue.markGranted : STR.access.queue.markRemoved}
          </button>
        </span>
      </li>
    );
  }

  function block(email: ItEmailPublic): ReactNode {
    const open = email.items.filter((item) => item.status === 'open');
    const setUp = open.filter((item) => item.action === 'set_up').length;
    const systems = [...new Set(open.map((item) => item.system.name))].join(', ');
    const sentAt = email.sentAt ?? email.createdAt;
    return (
      <li key={email.id} style={{ listStyle: 'none' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '24px 96px minmax(0,1fr) auto',
            gap: 10,
            alignItems: 'center',
            padding: '9px 16px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface-2)',
            fontSize: 12.5,
            ...(email.overdue ? { boxShadow: overdueBar } : {}),
          }}
        >
          <span />
          <span
            className={email.overdue ? 'tx-danger-strong' : 'tx-subtle'}
            style={{ fontSize: 11.5, fontWeight: email.overdue ? 700 : 400, fontVariantNumeric: 'tabular-nums' }}
          >
            {email.overdue ? STR.access.queue.overdueAge(relativeTime(sentAt)) : relativeTime(sentAt)}
          </span>
          <div style={{ minWidth: 0 }}>
            <div>
              <b style={{ fontWeight: 650 }}>{STR.itEmails.blockTitle}</b>
              {STR.itEmails.blockSent(formatDateTime(sentAt), email.sentBy?.name ?? email.createdBy.name)}
            </div>
            <div className="tx-subtle" style={{ fontSize: 11.5, marginTop: 2, ...ellipsis }}>
              {STR.itEmails.blockOpen(open.length, email.items.length, setUp, open.length - setUp)}{' '}
              {systems}
            </div>
          </div>
          <span style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="tbtn"
              style={rowActionStyle}
              onClick={() => {
                onOpen(email);
              }}
            >
              {STR.itEmails.openEmail}
            </button>
            <button
              type="button"
              className="tbtn"
              style={rowActionStyle}
              disabled={busy}
              onClick={() => {
                actions.mutate({ kind: 'confirmAll', email });
              }}
            >
              {STR.itEmails.confirmAll(open.length)}
            </button>
            <button
              type="button"
              className="tbtn"
              style={rowActionStyle}
              disabled={busy}
              onClick={() => {
                actions.mutate({ kind: 'takeBack', email });
              }}
            >
              {STR.itEmails.takeBack}
            </button>
          </span>
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {open.map((item) => itemLine(email, item))}
        </ul>
      </li>
    );
  }

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 16px',
          borderBottom: closed ? 'none' : '1px solid var(--border)',
          background: 'var(--surface-2)',
          fontSize: 13.5,
          fontWeight: 650,
        }}
      >
        <button
          type="button"
          className="tbtn"
          aria-expanded={!closed}
          onClick={() => {
            setClosed((value) => !value);
          }}
          style={{ ...quietButtonStyle, color: 'var(--text)', padding: '2px 6px' }}
        >
          {STR.itEmails.lane}{' '}
          <span className="tx-subtle" style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {openItems}
          </span>
        </button>
        {overdueItems > 0 ? (
          <span className="tx-danger-strong" style={{ fontSize: 12, fontWeight: 700 }}>
            {STR.itEmails.laneOverdue(overdueItems)}
          </span>
        ) : drafts.length > 0 ? (
          <span className="tx-subtle" style={{ fontSize: 12, fontWeight: 500 }}>
            {STR.itEmails.draftsNotSent(drafts.length)}
          </span>
        ) : null}
      </div>
      {closed ? null : (
        <>
          {overdueAfterDays === null ? null : (
            <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)' }}>
              <span className="tx-subtle" style={{ fontSize: 11.5 }}>
                {STR.itEmails.laneHelp(overdueAfterDays)}
              </span>
            </div>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {drafts.map((email) => (
              <li
                key={email.id}
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  padding: '9px 16px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: 12.5,
                }}
              >
                <span className="tx-subtle" style={{ fontSize: 11.5, width: 76 }}>
                  {STR.itEmails.draftLabel}
                </span>
                <span style={{ flex: 1, minWidth: 200 }}>
                  {STR.itEmails.draftRow(
                    email.items.length,
                    relativeTime(email.createdAt),
                    email.createdBy.name,
                  )}
                </span>
                <button
                  type="button"
                  className="tbtn"
                  style={rowActionStyle}
                  onClick={() => {
                    onOpen(email);
                  }}
                >
                  {STR.itEmails.openDraft}
                </button>
                <button
                  type="button"
                  className="tbtn tbtn-danger"
                  style={rowActionStyle}
                  disabled={busy}
                  onClick={() => {
                    actions.mutate({ kind: 'takeBack', email });
                  }}
                >
                  {STR.itEmails.discard}
                </button>
              </li>
            ))}
            {waiting.map(block)}
            {drafts.length === 0 && waiting.length === 0 ? (
              <li>
                <EmptyRow>{STR.itEmails.laneEmpty}</EmptyRow>
              </li>
            ) : null}
          </ul>
        </>
      )}
    </Card>
  );
}
